/**
 * Locale-Aware Link Component
 *
 * Wraps react-router-dom's <Link> to automatically prepend
 * the current locale prefix to all internal paths.
 *
 * Usage:
 *   <LocaleLink to="/campaigns">Campaigns</LocaleLink>
 *   // Renders: <Link to="/ar/campaigns"> when locale is 'ar'
 *
 * External links (starting with http:// or https://) pass through unchanged.
 * Admin routes (/admin/*) are not prefixed.
 */

import React from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { addLocaleToPath } from './index';

export interface LocaleLinkProps extends Omit<LinkProps, 'to'> {
  to: string;
}

export const LocaleLink: React.FC<LocaleLinkProps> = ({ to, ...props }) => {
  const { i18n } = useTranslation();
  const locale = i18n.language;

  // Don't prefix external links
  if (to.startsWith('http://') || to.startsWith('https://') || to.startsWith('mailto:')) {
    return <Link to={to} {...props} />;
  }

  // Don't prefix hash-only links
  if (to.startsWith('#')) {
    return <Link to={to} {...props} />;
  }

  const localizedPath = addLocaleToPath(to, locale);
  return <Link to={localizedPath} {...props} />;
};

export default LocaleLink;
